import { Component } from '@angular/core';
import { BaseIndexPageComponent } from '../../../components/pages/base-index-page/base-index-page.component';
import BaseIndexPageConfig from '../../../components/pages/base-index-page/base-index-page.config';
import { DataTableColumnText } from '../../../components/widgets/data-table/columns/data-table-column-text/data-table-column-text.config';
import { FileManagerAuditService } from '../../../services/api/file-manager-audit.service';
import { CommonService } from '../../../services/common.service';

const ACTION_LABELS: { [key: string]: string } = {
  UPLOAD: 'Upload',
  DOWNLOAD: 'Download',
  MOVE: 'Move',
  RENAME: 'Rename',
  CREATE: 'Create',
  DELETE: 'Delete'
};

const ACTION_BADGE_CLASSES: { [key: string]: string } = {
  UPLOAD: 'text-bg-success',
  DOWNLOAD: 'text-bg-info',
  MOVE: 'text-bg-primary',
  RENAME: 'text-bg-warning',
  CREATE: 'text-bg-secondary',
  DELETE: 'text-bg-danger'
};

const STATUS_LABELS: { [key: string]: string } = {
  SUCCESS: 'Success',
  FAILURE: 'Failure',
  IN_PROGRESS: 'In progress'
};

const PATH_CHANGE_ACTIONS = ['MOVE', 'RENAME', 'DELETE'];

@Component({
  selector: 'app-file-manager-audit-index-page',
  imports: [BaseIndexPageComponent],
  templateUrl: './file-manager-audit-index-page.component.html',
  styleUrl: './file-manager-audit-index-page.component.scss'
})
export class FileManagerAuditIndexPageComponent {
  baseIndexPageConfig: BaseIndexPageConfig;

  constructor(
    protected fileManagerAuditService: FileManagerAuditService,
    private commonService: CommonService
  ) {
    this.baseIndexPageConfig = {
      pageTitle: 'File Activity Log',
      cardTitle: 'File Activity Log',
      dataTable: {
        data: [],
        searchModel: {
          path: null,
          action: Object.keys(ACTION_LABELS).join(','),
          userId: null,
          dateFrom: null,
          dateTo: null,
          status: null
        },
        pagination: null,
        topInputFilters: [
          {
            label: 'Date From',
            attribute: 'dateFrom',
            type: 'date'
          },
          {
            label: 'Date To',
            attribute: 'dateTo',
            type: 'date'
          }
        ],
        columns: [
          new DataTableColumnText({
            attribute: 'createTime',
            header: 'DATE/TIME',
            width: '180px',
            value: (item: any) => item.createTime ? this.commonService.formatDate(new Date(item.createTime)) : ''
          }),
          new DataTableColumnText({
            attribute: 'action',
            header: 'OPERATION TYPE',
            filter: 'multiselect',
            filterOptionsSource: 'fileManagerAuditAction',
            value: (item: any) => ACTION_LABELS[item.action] ?? item.action,
            badges: (item: any) => [{
              label: (ACTION_LABELS[item.action] ?? item.action).toUpperCase(),
              cssClass: ACTION_BADGE_CLASSES[item.action] ?? 'text-bg-secondary'
            }]
          }),
          new DataTableColumnText({
            attribute: 'path',
            header: 'FILE/FOLDER',
            filter: 'text',
            value: (item: any) => item.fileNameOriginal ? `${item.fileNameOriginal} — ${item.finalKey}` : item.finalKey,
            badgesLayout: 'vertical',
            badges: (item: any) => {
              const badges = [];
              if (item.fileNameOriginal) {
                badges.push({ label: item.fileNameOriginal, cssClass: 'bg-dark-subtle text-dark-emphasis fw-semibold' });
              }
              if (item.finalKey) {
                badges.push({ label: item.finalKey, cssClass: 'bg-light text-body-secondary border' });
              }
              return badges;
            }
          }),
          new DataTableColumnText({
            attribute: 'fromTo',
            header: 'SOURCE → DESTINATION',
            value: (item: any) => {
              if (!PATH_CHANGE_ACTIONS.includes(item.action)) {
                return '';
              }
              return `${item.initialKey ?? ''} → ${item.finalKey ?? ''}`;
            },
            badgesLayout: 'vertical',
            badgesSeparator: 'bi bi-arrow-down',
            badges: (item: any) => {
              if (!PATH_CHANGE_ACTIONS.includes(item.action)) {
                return [];
              }
              return [
                { label: item.initialKey ?? '', cssClass: 'bg-secondary-subtle text-secondary-emphasis' },
                { label: item.finalKey ?? '', cssClass: 'bg-primary-subtle text-primary-emphasis' }
              ];
            }
          }),
          new DataTableColumnText({
            attribute: 'userId',
            header: 'USER',
            filter: 'select',
            filterOptionsSource: 'staffUsers',
            value: (item: any) => item.user ? `${item.user.firstName} ${item.user.lastName}` : ''
          }),
          new DataTableColumnText({
            attribute: 'status',
            header: 'STATUS',
            filter: 'select',
            filterOptionsSource: 'fileManagerAuditStatus',
            value: (item: any) => STATUS_LABELS[item.status] ?? item.status
          })
        ]
      },
      apiListService: (options: any) => this.fileManagerAuditService.list(options),
      responseListModelName: 'fileManagerAudits'
    };
  }
}
